import { Check, ArrowRight } from "lucide-react";
import { useContext } from "react";
import { Reveal, SectionTag, BigHead } from "./Reveal";
import { openContact } from "./ContactDialog";
import { ContentCtx } from "@/lib/content";

const TIERS = [
  {
    name: "Stay Live", tier: "Tier 1", price: 150, bg: "bg-staxyellow", text: "text-ink", btn: "bg-ink text-white",
    features: ["Hosting & uptime monitoring", "Monthly updates & backups", "Security checks", "1 content tweak / month"],
  },
  {
    name: "Growth", tier: "Tier 2", price: 300, bg: "bg-staxcyan", text: "text-ink", btn: "bg-ink text-white", pop: true,
    features: ["Everything in Stay Live", "4 content updates / month", "Monthly SEO check-in", "Performance reports", "Priority support"],
  },
  {
    name: "Elite Partner", tier: "Tier 3", price: 550, bg: "bg-staxmagenta", text: "text-white", btn: "bg-white text-ink",
    features: ["Everything in Growth", "Unlimited small updates", "Quarterly strategy call", "New page design included", "Same-day support"],
  },
];

export const MaintenanceTiers = ({ onSelect }) => {
  const content = useContext(ContentCtx);
  return (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 items-stretch">
    {TIERS.map((t, i) => {
      const price = content?.tiers?.[i] ?? t.price;
      return (
      <div
        key={t.name}
        data-testid={`tier-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
        className={`relative flex h-full flex-col border-4 border-ink ${t.bg} ${t.text} shadow-hard transition-transform hover:-translate-y-2`}
      >
        {t.pop && (
          <span className="absolute -top-4 left-1/2 -translate-x-1/2 border-2 border-ink bg-ink px-4 py-1 font-mono2 text-[10px] font-bold uppercase tracking-[0.25em] text-staxyellow whitespace-nowrap">
            Most Popular
          </span>
        )}
        <div className="border-b-4 border-ink px-7 py-6">
          <p className="font-mono2 text-xs font-bold uppercase tracking-[0.3em] opacity-60">{t.tier}</p>
          <h3 className="mt-1 font-display uppercase text-3xl">{t.name}</h3>
          <p className="mt-3 font-display text-5xl">
            ${price}<span className="font-mono2 text-sm font-bold tracking-widest opacity-60">/mo</span>
          </p>
        </div>
        <ul className="flex-1 space-y-3 px-7 py-7">
          {t.features.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-sm font-semibold">
              <Check size={17} className="mt-0.5 shrink-0" strokeWidth={3} /> {f}
            </li>
          ))}
        </ul>
        <div className="px-7 pb-7">
          <button
            data-testid={`tier-cta-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => { onSelect?.(); openContact(`Hi! I'd like to talk about the ${t.name} maintenance plan ($${t.price}/mo).`); }}
            className={`group flex w-full items-center justify-center gap-2 border-2 border-ink px-5 py-3.5 font-display uppercase text-sm ${t.btn} transition-transform hover:-translate-y-0.5`}
          >
            View Maintenance Plans <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </div>
      );
    })}
  </div>
  );
};

export const Maintenance = () => (
  <section id="maintenance" data-testid="maintenance-section" className="relative overflow-hidden bg-paper py-24 sm:py-32">
    <div className="ink-dot absolute -right-32 -top-24 h-80 w-80 bg-staxyellow" />
    <div className="relative mx-auto max-w-7xl px-4 sm:px-8">
      <div className="text-center">
        <Reveal><SectionTag color="bg-staxcyan text-ink">Website Maintenance</SectionTag></Reveal>
        <Reveal delay={0.08}>
          <BigHead className="mt-6">Keep Your Site<br />Staxed<span className="text-staxmagenta">.</span></BigHead>
        </Reveal>
        <Reveal delay={0.14}>
          <p className="mx-auto mt-5 max-w-md font-medium text-ink/60 text-base sm:text-lg">
            Your site stays fast, secure, and fresh — you stay focused on the business.
          </p>
        </Reveal>
      </div>
      <div className="mt-14">
        <MaintenanceTiers />
      </div>
    </div>
  </section>
);
